/**
 * Pricing Rules types for Partner Dashboard
 *
 * Core types are derived from the generated admin GraphQL operation types (not the raw
 * schema types), so they stay exactly in sync with what each operation actually selects.
 * Most operations' request/response shapes come directly from
 * `@src/types/__generated/rewareAdminTypes` at their call sites; this file only holds types
 * shared across multiple files (or without a schema equivalent).
 */

import type {
  GetPricingRulesQuery,
  GetPricingHistoryQuery,
  CreatePricingRuleMutationVariables,
  UpdatePricingRuleMutationVariables,
  GenerateResalePricingExportMutation,
} from '@src/types/__generated/rewareAdminTypes';

// Core types derived from generated admin operation types

type PricingRulesConnection = NonNullable<
  NonNullable<GetPricingRulesQuery['partner']>['pricingRules']
>;

export type PricingRule = PricingRulesConnection['edges'][number]['node'];
export type PageInfo = PricingRulesConnection['pageInfo'];

export type PricingRuleInput = CreatePricingRuleMutationVariables['rule'];
export type PricingRuleUpdateInput = UpdatePricingRuleMutationVariables['updates'];

export type PricingExportResult = NonNullable<
  NonNullable<GenerateResalePricingExportMutation['partner']>['generateResalePricingExport']
>;

// Dashboard-specific types

export const PRICING_TYPES = ['yerdle_price', 'condition'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

export const PRICING_TYPE_LABELS: Record<PricingType, string> = {
  yerdle_price: 'Yerdle Price',
  condition: 'Condition Discount',
};

export const SOURCE_COLUMNS = [
  'price',
  'msrp_price',
  'last_known_retail_price',
  'last_known_outlet_price',
  'absolute_price',
] as const;

export interface PricingFilters {
  department?: string;
  category?: string;
  tag?: string;
  name?: string;
  merchantCategory?: string;
  upc?: string;
  condition?: string;
}

export interface StartPricingImportResult {
  pricingDiscountId: number;
}

// Pricing history entry, derived from the getPricingHistory query's own generated
// result type (not the raw schema type) so it stays exactly in sync with what that
// query actually selects.
export type PricingHistoryEntry = NonNullable<
  NonNullable<NonNullable<GetPricingHistoryQuery['partner']>['pricingHistory']>[number]
>;
